import type { ReactNode, SelectHTMLAttributes } from "react";
import { ChevronDown } from "@untitledui/icons";
import { cx } from "@/utils/cx";

/**
 * Native `<select>` v UUI vzhledu — potlačí systémovou šipku (`appearance-none`)
 * a vykreslí `ChevronDown` s odsazením od okraje; `pr-9` na selectu drží text
 * mimo ikonu. Bez toho se u raw `<select>` kreslí systémová šipka nalepená
 * na rámeček (padding ji v Chrome/WebKitu neodsune).
 *
 * Vizuální specifikace (pozice, velikost a barva ikony) je převzatá z UUI
 * `NativeSelect` ({@link ../base/select/select-native.tsx}). Nepoužíváme ho
 * přímo, protože bere volby jako pole `options`, kdežto naše volací místa
 * renderují `<option>` jako children (často podmíněně nebo z mapy) a sedí
 * uvnitř vlastních `Field` / `Filter` obalů s vlastním labelem.
 */
interface AppSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size" | "children"> {
  /** `true` (default) = přes celou šířku, `false` = podle obsahu (filtrační lišty). */
  fullWidth?: boolean;
  /** Doplňkové třídy `<select>` — mergují se přes `cx`, takže lze přepsat např. padding. */
  className?: string;
  /** Doplňkové třídy obalu (pozicuje ikonu). */
  wrapperClassName?: string;
  children: ReactNode;
}

const selectBase =
  "appearance-none rounded-lg bg-primary py-2 pr-9 pl-3 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-50";

export function AppSelect({ fullWidth = true, className, wrapperClassName, children, ...props }: AppSelectProps) {
  return (
    <div className={cx("relative grid items-center", fullWidth ? "w-full" : "w-fit", wrapperClassName)}>
      <select {...props} className={cx(selectBase, fullWidth && "w-full", className)}>
        {children}
      </select>
      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 size-4 stroke-[2.25px] text-fg-quaternary" />
    </div>
  );
}
